// =============================================================================
// Admin ProfilePage — User profile management
// =============================================================================
// Sections:
//   1. Profile Information — avatar, name, email (read-only for now)
//   2. Change Password — current + new password with validation
//   3. Account Actions — sign out
// =============================================================================

import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/hooks/use-auth';
import { api } from '@/lib/api';
import { Card, CardHeader, Input, Button, Avatar, Badge } from '@tkpod/ui';

// ---------------------------------------------------------------------------
// Password validation helpers
// ---------------------------------------------------------------------------

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
}

function getPasswordStrength(password: string): PasswordStrength {
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;
  score = Math.min(score, 4);

  const levels: Record<number, { label: string; color: string }> = {
    0: { label: 'Very Weak', color: 'bg-danger' },
    1: { label: 'Weak', color: 'bg-danger' },
    2: { label: 'Fair', color: 'bg-warning' },
    3: { label: 'Strong', color: 'bg-success' },
    4: { label: 'Very Strong', color: 'bg-success' },
  };

  return { score, ...levels[score] };
}

function validateNewPassword(password: string): string | null {
  if (password.length < 8) return 'Password must be at least 8 characters';
  if (!/[A-Z]/.test(password)) return 'Must contain at least one uppercase letter';
  if (!/[a-z]/.test(password)) return 'Must contain at least one lowercase letter';
  if (!/\d/.test(password)) return 'Must contain at least one number';
  if (!/[^a-zA-Z0-9]/.test(password)) return 'Must contain at least one special character';
  return null;
}

// ---------------------------------------------------------------------------
// ProfilePage Component
// ---------------------------------------------------------------------------

export function ProfilePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'Admin';
  const passwordStrength = newPassword ? getPasswordStrength(newPassword) : null;
  const newPasswordError = newPassword ? validateNewPassword(newPassword) : null;
  const confirmError = confirmPassword && newPassword !== confirmPassword
    ? 'Passwords do not match'
    : null;

  const canSubmitPassword =
    currentPassword.length > 0 &&
    newPassword.length >= 8 &&
    !newPasswordError &&
    !confirmError &&
    confirmPassword.length > 0 &&
    !passwordLoading;

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmitPassword) return;

    setPasswordLoading(true);
    setPasswordError(null);
    setPasswordSuccess(false);

    try {
      // noRetry: true prevents the api-client from treating a 401 (wrong password)
      // as a session expiry and triggering automatic token refresh + logout.
      await api.post('/api/v1/auth/change-password', {
        currentPassword,
        newPassword,
      }, { noRetry: true });

      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      setTimeout(async () => {
        await logout();
        navigate('/login', { replace: true });
      }, 2000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to change password';
      setPasswordError(message);
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* ================================================================= */}
      {/* Section 1: Profile Information                                     */}
      {/* ================================================================= */}
      <Card>
        <CardHeader
          title="Profile Information"
          description="Your personal details and account information."
        />

        <div className="mt-6 flex flex-col items-start gap-6 sm:flex-row sm:items-center">
          <div className="flex flex-col items-center gap-2">
            <Avatar src={user?.avatar} name={displayName} size="xl" />
            <p className="text-xs text-text-tertiary">Avatar</p>
          </div>

          <div className="flex-1 space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="First Name"
                value={user?.firstName ?? ''}
                disabled
                hint="Contact support to update your name"
              />
              <Input
                label="Last Name"
                value={user?.lastName ?? ''}
                disabled
              />
            </div>
            <Input
              label="Email Address"
              type="email"
              value={user?.email ?? ''}
              disabled
              hint="Email changes are not supported yet"
            />
          </div>
        </div>
      </Card>

      {/* ================================================================= */}
      {/* Section 2: Organization & Role                                     */}
      {/* ================================================================= */}
      {user?.currentOrganization && (
        <Card>
          <CardHeader
            title="Organization"
            description="Your current workspace and assigned role."
          />
          <div className="mt-4 rounded-lg border border-border bg-surface-secondary p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-text-primary">
                  {user.currentOrganization.name}
                </p>
                <p className="mt-0.5 text-xs text-text-tertiary">
                  {user.currentOrganization.slug}
                </p>
              </div>
              <Badge variant="primary">{user.currentOrganization.role}</Badge>
            </div>

            {user.currentOrganization.permissions.length > 0 && (
              <div className="mt-3 border-t border-border pt-3">
                <p className="text-xs font-medium text-text-secondary mb-2">
                  Permissions ({user.currentOrganization.permissions.length})
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {user.currentOrganization.permissions.slice(0, 8).map((perm) => (
                    <span
                      key={perm}
                      className="inline-flex rounded-md bg-surface-tertiary px-2 py-0.5 text-xs text-text-secondary"
                    >
                      {perm}
                    </span>
                  ))}
                  {user.currentOrganization.permissions.length > 8 && (
                    <span className="inline-flex rounded-md bg-surface-tertiary px-2 py-0.5 text-xs text-text-tertiary">
                      +{user.currentOrganization.permissions.length - 8} more
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* ================================================================= */}
      {/* Section 3: Change Password                                         */}
      {/* ================================================================= */}
      <Card>
        <CardHeader
          title="Change Password"
          description="Update your password. You will be signed out after the change."
        />

        <form onSubmit={handlePasswordChange} className="mt-6 max-w-md space-y-4">
          {passwordSuccess && (
            <div className="rounded-md bg-success/10 px-4 py-3 text-sm text-success">
              Password changed successfully! Redirecting to login...
            </div>
          )}

          {passwordError && (
            <div className="rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
              {passwordError}
            </div>
          )}

          <Input
            label="Current Password"
            type="password"
            value={currentPassword}
            onChange={(e) => { setCurrentPassword(e.target.value); setPasswordError(null); }}
            placeholder="Enter your current password"
            autoComplete="current-password"
            required
          />

          <div>
            <Input
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); setPasswordError(null); setPasswordSuccess(false); }}
              placeholder="Enter a strong new password"
              autoComplete="new-password"
              error={newPassword && newPasswordError ? newPasswordError : undefined}
              required
            />

            {passwordStrength && (
              <div className="mt-2">
                <div className="flex gap-1">
                  {[0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className={`h-1 flex-1 rounded-full transition-colors ${
                        i < passwordStrength.score ? passwordStrength.color : 'bg-surface-tertiary'
                      }`}
                    />
                  ))}
                </div>
                <p className="mt-1 text-xs text-text-tertiary">{passwordStrength.label}</p>
              </div>
            )}
          </div>

          <Input
            label="Confirm New Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => { setConfirmPassword(e.target.value); setPasswordError(null); }}
            placeholder="Re-enter your new password"
            autoComplete="new-password"
            error={confirmError ?? undefined}
            required
          />

          <div className="rounded-lg bg-surface-secondary p-3">
            <p className="text-xs font-medium text-text-secondary mb-2">Password Requirements</p>
            <ul className="space-y-1">
              <PasswordRequirement met={newPassword.length >= 8} label="At least 8 characters" />
              <PasswordRequirement met={/[A-Z]/.test(newPassword)} label="One uppercase letter" />
              <PasswordRequirement met={/[a-z]/.test(newPassword)} label="One lowercase letter" />
              <PasswordRequirement met={/\d/.test(newPassword)} label="One number" />
              <PasswordRequirement met={/[^a-zA-Z0-9]/.test(newPassword)} label="One special character" />
            </ul>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button type="submit" disabled={!canSubmitPassword} loading={passwordLoading}>
              Update Password
            </Button>
            {currentPassword && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                  setPasswordError(null);
                  setPasswordSuccess(false);
                }}
              >
                Cancel
              </Button>
            )}
          </div>
        </form>
      </Card>

      {/* ================================================================= */}
      {/* Section 4: Account Actions                                         */}
      {/* ================================================================= */}
      <Card>
        <CardHeader title="Account" description="Manage your account session." />
        <div className="mt-4 flex items-center justify-between rounded-lg border border-border bg-surface-secondary p-4">
          <div>
            <p className="text-sm font-medium text-text-primary">Sign Out</p>
            <p className="text-xs text-text-tertiary">End your current session on this device.</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => { await logout(); navigate('/login', { replace: true }); }}
          >
            Sign Out
          </Button>
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Password requirement checklist item
// ---------------------------------------------------------------------------

function PasswordRequirement({ met, label }: { met: boolean; label: string }) {
  return (
    <li className="flex items-center gap-2 text-xs">
      {met ? (
        <svg className="h-3.5 w-3.5 text-success" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
        </svg>
      ) : (
        <svg className="h-3.5 w-3.5 text-text-tertiary" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <circle cx="12" cy="12" r="9" />
        </svg>
      )}
      <span className={met ? 'text-text-primary' : 'text-text-tertiary'}>{label}</span>
    </li>
  );
}
